import { useCallback, useState } from "react";
import type { TenantBootstrap } from "../../../tenant/bootstrap.js";
import { adoptSession, type AuthSession } from "../../../auth/keycloak.js";
import { signInViaPopup, WebglSignInError, type WebglSignInFailure } from "../../../auth/webgl-popup-auth.js";

/**
 * The panel's own sign-in lifecycle (ADR-050 Phase 2).
 *
 * The panel is the one surface in this app that cannot redirect itself to Keycloak — it is a frame inside Unity's
 * page, and `main.tsx` deliberately skips `initAuth` there (ADR-044 D6). So the session is acquired by popup on an
 * explicit click, and this hook owns the three states that produces.
 *
 * **`busy` is not cosmetic.** `window.open` needs a user activation, so a second click while the first popup is
 * still open would either be swallowed or stack a second window on the same named target. Disabling the control
 * for the duration is what keeps one gesture to one popup.
 */
export type WebglSignInState =
  | { status: "idle" }
  | { status: "busy" }
  | { status: "failed"; reason: WebglSignInReason };

/**
 * Everything the UI must be able to explain.
 *
 * `session-rejected` is this layer's own: the popup completed and the code exchanged, but Keycloak refused the
 * resulting refresh token when the adapter validated it (`adoptSession`). Distinct from `exchange-failed` because
 * the two fail at different hops and a retry means something different in each.
 */
export type WebglSignInReason = WebglSignInFailure | "session-rejected";

export interface WebglSignIn {
  /** The session this hook obtained, or null until one is. */
  session: AuthSession | null;
  state: WebglSignInState;
  /** Must be called from a user gesture — see `busy` above. */
  signIn: () => void;
}

/**
 * @param tenant the tenant to authenticate against, or **null when signing in here would be wrong** — a top-level
 *   tab, where `main.tsx` already runs the ordinary redirect flow (ADR-050 D7). Null disables the affordance
 *   entirely rather than hiding it, so there is no state in which the button exists but cannot work.
 */
export function useWebglSignIn(tenant: TenantBootstrap | null): WebglSignIn {
  const [session, setSession] = useState<AuthSession | null>(null);
  const [state, setState] = useState<WebglSignInState>({ status: "idle" });

  const signIn = useCallback(() => {
    if (!tenant) return;
    setState({ status: "busy" });

    // Deliberately NOT awaited by the caller: this runs from a click handler, and the popup can sit open for
    // minutes. The terminal `.catch` is the house pattern — an unhandled rejection here would surface as a
    // console error on a path the UI already reports properly.
    void (async () => {
      try {
        const tokens = await signInViaPopup(tenant);
        const next = await adoptSession(tenant, tokens);
        if (!next) {
          setState({ status: "failed", reason: "session-rejected" });
          return;
        }
        setSession(next);
        setState({ status: "idle" });
      } catch (error) {
        setState({
          status: "failed",
          reason: error instanceof WebglSignInError ? error.reason : "exchange-failed",
        });
      }
    })().catch(() => undefined);
  }, [tenant]);

  return { session, state, signIn };
}

/**
 * The catalogue key explaining a failure.
 *
 * A total `switch` over the union, so adding a reason without a message is a TYPE error rather than a panel that
 * silently renders an empty alert. The return type is the literal union rather than `string` deliberately: `t` is
 * typed against the catalogue, and a widened `string` would force a `defaultValue` at the call site — i.e. an
 * English fallback living in a component, which is exactly what ADR-048 removed from this codebase.
 */
export function signInFailureKey(
  reason: WebglSignInReason,
):
  | "webgl.signInBlocked"
  | "webgl.signInCancelled"
  | "webgl.signInTimedOut"
  | "webgl.signInDenied"
  | "webgl.signInFailed" {
  switch (reason) {
    case "popup-blocked":
      return "webgl.signInBlocked";
    case "cancelled":
      return "webgl.signInCancelled";
    case "timeout":
      return "webgl.signInTimedOut";
    case "denied":
      return "webgl.signInDenied";
    case "state-mismatch":
    case "exchange-failed":
    case "session-rejected":
      return "webgl.signInFailed";
  }
}
